/**
 * Lectura de elevación real desde el modelo digital de elevación (DEM).
 *
 * Fuente: AWS Terrain Tiles, formato Terrarium — mosaicos PNG donde cada píxel
 * codifica la altura del terreno en sus canales de color:
 *
 *     altura_m = (R * 256 + G + B / 256) - 32768
 *
 * Es el reemplazo abierto de Mapbox Terrain-RGB: sin API key, sin límite de
 * uso práctico, cacheable indefinidamente (ver AD-001 en docs/decisiones.md).
 *
 * REGLA QUE NO SE ROMPE (AD-004): si un dato no se puede leer, se informa el
 * hueco. Nunca se interpola entre origen y destino, nunca se estima, nunca se
 * rellena con el aeródromo más cercano. Un perfil de terreno inventado se ve
 * igual que uno real, y esa es exactamente la razón por la que es peligroso.
 *
 * PRECISIÓN — leer antes de confiar:
 *   · Los datos vienen mayormente de SRTM: ~30 m de resolución horizontal y
 *     error vertical típico de ±10 a ±16 m.
 *   · El DEM describe EL TERRENO. No contiene torres, antenas, tendidos
 *     eléctricos ni cables. Para vuelo a baja altura, los cables son el
 *     obstáculo que mata y ningún DEM público los tiene. Esto no sustituye
 *     cartas de obstáculos ni el reconocimiento previo de la ruta.
 */

const TERRARIUM_URL = (z, x, y) =>
  /* Por el proxio propio, como el resto de las capas: así queda en la caché
     del servidor y la elevación sigue funcionando sin cobertura.

     Iba directo a Amazon, y además de no cachearse, el CSP lo bloqueaba. */
  `/mapas/terrarium/${z}/${x}/${y}.png`;

const TAM_MOSAICO = 256;

/** z=13 ≈ 19 m/píxel en el ecuador: suficiente para resolver una quebrada
 *  sin disparar cientos de descargas por ruta. Máximo disponible: 15. */
export const ZOOM_DEM = 13;

const M_A_PIES = 3.28084;

/** Caché en memoria de mosaicos ya decodificados: clave "z/x/y" → Float32Array. */
const cache = new Map();
/** Descargas en curso, para no pedir dos veces el mismo mosaico. */
const enVuelo = new Map();

const LIMITE_CACHE = 400; // ~100 MB en el peor caso; suficiente para varias rutas

function claveMosaico(z, x, y) {
  return `${z}/${x}/${y}`;
}

/** lat/lon → coordenadas de píxel continuas dentro del esquema Web Mercator. */
function aPixel(lat, lon, z) {
  const n = TAM_MOSAICO * Math.pow(2, z);
  const x = ((lon + 180) / 360) * n;
  const latRad = (Math.max(-85.05112878, Math.min(85.05112878, lat)) * Math.PI) / 180;
  const y =
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n;
  return { x, y };
}

/** Descarga y decodifica un mosaico a un arreglo de alturas en metros. */
async function cargarMosaico(z, x, y) {
  const clave = claveMosaico(z, x, y);

  const cacheado = cache.get(clave);
  if (cacheado) return cacheado;

  const pendiente = enVuelo.get(clave);
  if (pendiente) return pendiente;

  const promesa = (async () => {
    const img = new Image();
    img.crossOrigin = "anonymous"; // sin esto, el canvas queda "manchado" y no se puede leer

    await new Promise((resolver, rechazar) => {
      img.onload = resolver;
      img.onerror = () =>
        rechazar(new Error(`No se pudo descargar el mosaico de terreno ${clave}`));
      img.src = TERRARIUM_URL(z, x, y);
    });

    const lienzo = document.createElement("canvas");
    lienzo.width = TAM_MOSAICO;
    lienzo.height = TAM_MOSAICO;
    const ctx = lienzo.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("El navegador no permitió leer el mosaico de terreno.");

    ctx.drawImage(img, 0, 0);
    const { data } = ctx.getImageData(0, 0, TAM_MOSAICO, TAM_MOSAICO);

    const alturas = new Float32Array(TAM_MOSAICO * TAM_MOSAICO);
    for (let i = 0, p = 0; i < alturas.length; i++, p += 4) {
      alturas[i] = data[p] * 256 + data[p + 1] + data[p + 2] / 256 - 32768;
    }

    if (cache.size >= LIMITE_CACHE) {
      // Descarta el más viejo (Map conserva orden de inserción).
      cache.delete(cache.keys().next().value);
    }
    cache.set(clave, alturas);
    return alturas;
  })();

  enVuelo.set(clave, promesa);
  try {
    return await promesa;
  } finally {
    enVuelo.delete(clave);
  }
}

/**
 * Elevación del terreno en un punto, en metros.
 * Interpolación bilineal entre los cuatro píxeles vecinos: sin esto el perfil
 * sale escalonado, con saltos artificiales de decenas de metros.
 *
 * @returns {Promise<number>} metros sobre el nivel medio del mar
 * @throws si el mosaico no se pudo descargar (sin red, o fuera de cobertura)
 */
export async function elevacionMetros(lat, lon, z = ZOOM_DEM) {
  const { x, y } = aPixel(lat, lon, z);

  const mx = Math.floor(x / TAM_MOSAICO);
  const my = Math.floor(y / TAM_MOSAICO);

  const px = x - mx * TAM_MOSAICO;
  const py = y - my * TAM_MOSAICO;

  const x0 = Math.floor(px);
  const y0 = Math.floor(py);
  const fx = px - x0;
  const fy = py - y0;

  // Los cuatro vecinos pueden caer en mosaicos distintos si el punto está en
  // el borde. Se resuelve cada uno con su propio mosaico.
  const leer = async (ix, iy) => {
    let tx = mx;
    let ty = my;
    let cx = ix;
    let cy = iy;

    if (cx >= TAM_MOSAICO) { tx += 1; cx -= TAM_MOSAICO; }
    if (cy >= TAM_MOSAICO) { ty += 1; cy -= TAM_MOSAICO; }

    const mosaico = await cargarMosaico(z, tx, ty);
    return mosaico[cy * TAM_MOSAICO + cx];
  };

  const [h00, h10, h01, h11] = await Promise.all([
    leer(x0, y0),
    leer(x0 + 1, y0),
    leer(x0, y0 + 1),
    leer(x0 + 1, y0 + 1),
  ]);

  const arriba = h00 * (1 - fx) + h10 * fx;
  const abajo = h01 * (1 - fx) + h11 * fx;
  return arriba * (1 - fy) + abajo * fy;
}

/** Elevación en pies, que es la unidad con la que trabaja el resto de la app. */
export async function elevacionPies(lat, lon, z = ZOOM_DEM) {
  const m = await elevacionMetros(lat, lon, z);
  return Math.round(m * M_A_PIES);
}

/* ------------------------------------------------------------------------ */
/* Geodesia                                                                  */
/* ------------------------------------------------------------------------ */

const RADIO_TIERRA_NM = 3440.065;

/** Distancia ortodrómica en millas náuticas. */
export function distanciaNm(a, b) {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLon = (b.lon - a.lon) * rad;
  const lat1 = a.lat * rad;
  const lat2 = b.lat * rad;

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * RADIO_TIERRA_NM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Rumbo verdadero inicial, en grados. */
export function rumboVerdadero(a, b) {
  const rad = Math.PI / 180;
  const lat1 = a.lat * rad;
  const lat2 = b.lat * rad;
  const dLon = (b.lon - a.lon) * rad;

  const y = Math.sin(dLon) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return (Math.atan2(y, x) / rad + 360) % 360;
}

/** Punto intermedio sobre el círculo máximo, con t entre 0 y 1. */
export function puntoIntermedio(a, b, t) {
  const rad = Math.PI / 180;
  const lat1 = a.lat * rad;
  const lon1 = a.lon * rad;
  const lat2 = b.lat * rad;
  const lon2 = b.lon * rad;

  const d = distanciaNm(a, b) / RADIO_TIERRA_NM;
  if (d === 0) return { lat: a.lat, lon: a.lon };

  const A = Math.sin((1 - t) * d) / Math.sin(d);
  const B = Math.sin(t * d) / Math.sin(d);

  const x = A * Math.cos(lat1) * Math.cos(lon1) + B * Math.cos(lat2) * Math.cos(lon2);
  const y = A * Math.cos(lat1) * Math.sin(lon1) + B * Math.cos(lat2) * Math.sin(lon2);
  const z = A * Math.sin(lat1) + B * Math.sin(lat2);

  return {
    lat: Math.atan2(z, Math.sqrt(x * x + y * y)) / rad,
    lon: Math.atan2(y, x) / rad,
  };
}

/* ------------------------------------------------------------------------ */
/* Perfil de ruta                                                            */
/* ------------------------------------------------------------------------ */

/**
 * Perfil de elevación real a lo largo de una ruta.
 *
 * @param {Array<{lat:number, lon:number, name?:string}>} puntos  waypoints en orden
 * @param {object} opciones
 * @param {number} opciones.pasoNm       separación entre muestras (por defecto 0.5 NM ≈ 926 m)
 * @param {number} opciones.maxMuestras  tope duro de muestras
 * @param {(hechas:number, total:number)=>void} opciones.onProgreso
 * @returns {Promise<{muestras:Array, distanciaTotalNm:number, huecos:number, cimaFt:number|null}>}
 *
 * Cada muestra: { distNm, lat, lon, elevFt|null, wpName }
 * `elevFt: null` significa que ese punto NO se pudo leer. Se dibuja como hueco,
 * jamás se rellena.
 */
export async function perfilDeRuta(puntos, opciones = {}) {
  const { pasoNm = 0.5, maxMuestras = 600, onProgreso } = opciones;

  const validos = puntos.filter(
    (p) => Number.isFinite(p?.lat) && Number.isFinite(p?.lon),
  );
  if (validos.length < 2) {
    throw new Error("Hacen falta al menos dos puntos con coordenadas.");
  }

  // 1. Armar la lista de muestras a lo largo de cada tramo.
  const muestras = [];
  let acumuladoNm = 0;

  for (let i = 0; i < validos.length - 1; i++) {
    const a = validos[i];
    const b = validos[i + 1];
    const tramoNm = distanciaNm(a, b);
    const n = Math.max(1, Math.ceil(tramoNm / pasoNm));

    for (let k = 0; k < n; k++) {
      const t = k / n;
      const p = puntoIntermedio(a, b, t);
      muestras.push({
        distNm: acumuladoNm + tramoNm * t,
        lat: p.lat,
        lon: p.lon,
        elevFt: null,
        wpName: k === 0 ? a.name || null : null,
      });
    }
    acumuladoNm += tramoNm;
  }

  const ultimo = validos[validos.length - 1];
  muestras.push({
    distNm: acumuladoNm,
    lat: ultimo.lat,
    lon: ultimo.lon,
    elevFt: null,
    wpName: ultimo.name || null,
  });

  // Si la ruta es muy larga, se ralea de forma pareja en vez de recortar el final.
  let finales = muestras;
  if (muestras.length > maxMuestras) {
    const salto = muestras.length / maxMuestras;
    finales = Array.from({ length: maxMuestras }, (_, i) => muestras[Math.floor(i * salto)]);
    finales[finales.length - 1] = muestras[muestras.length - 1];
  }

  // 2. Leer la elevación en lotes: en paralelo total el navegador se satura,
  //    de a uno tarda una eternidad.
  const LOTE = 24;
  let huecos = 0;

  for (let i = 0; i < finales.length; i += LOTE) {
    const lote = finales.slice(i, i + LOTE);
    await Promise.all(
      lote.map(async (m) => {
        try {
          m.elevFt = await elevacionPies(m.lat, m.lon);
        } catch {
          m.elevFt = null; // hueco explícito — ver AD-004
          huecos += 1;
        }
      }),
    );
    onProgreso?.(Math.min(i + LOTE, finales.length), finales.length);
  }

  const conDato = finales.filter((m) => m.elevFt !== null);
  const cimaFt = conDato.length
    ? conDato.reduce((max, m) => (m.elevFt > max ? m.elevFt : max), -Infinity)
    : null;

  return {
    muestras: finales,
    distanciaTotalNm: acumuladoNm,
    huecos,
    cimaFt,
  };
}

/** Vacía la caché de mosaicos. Útil al liberar memoria en dispositivos chicos. */
export function limpiarCacheTerreno() {
  cache.clear();
}
